import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, updateDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import FirestoreService from '../assets/js/firestore_instance';

const AdminDialog = ({ isOpen, onClose, organization }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const auth = getAuth();
  const db = FirestoreService.getFirestore();

  useEffect(() => {
    if (isOpen) {
      fetchAdmins();
    }
  }, [isOpen]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "admin_users"),
        where("organizationId", "array-contains", organization.organizationId)
      );
      const querySnapshot = await getDocs(q);
      const adminsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdmins(adminsList);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
        // Find user in users collection
        const userQuery = query(
        collection(db, "users"),
        where("email", "==", newAdminEmail)
        );
        const userSnapshot = await getDocs(userQuery);

        if (userSnapshot.empty) {
        toast.error('User not found');
        return;
        }

        const userDoc = userSnapshot.docs[0];
        const userData = userDoc.data();

        // Check if user exists in admin_users
        const adminUserRef = doc(db, "admin_users", userDoc.id);
        const adminUserSnap = await getDoc(adminUserRef);

        if (adminUserSnap.exists()) {
        // Update existing admin user
        const currentOrgIds = adminUserSnap.data().organizationId || [];
        if (!currentOrgIds.includes(organization.organizationId)) {
            await updateDoc(adminUserRef, {
            organizationId: [...currentOrgIds, organization.organizationId]
            });
        }
        } else {
        // Create new admin user
        await setDoc(adminUserRef, {
            displayName: userData.name,
            email: userData.email,
            photoUrl: userData.photourl,
            organizationId: [organization.organizationId]
        });
        }

        toast.success('Admin added successfully');
        setNewAdminEmail('');
        fetchAdmins();
    } catch (err) {
        console.error(err);
        toast.error('Failed to add admin');
    }
    };

  const handleRemoveAdmin = async (adminId) => {
    console.log(adminId);
    if (adminId === auth.currentUser.uid) {
      toast.error('You cannot remove yourself as admin');
      return;
    }

    try {
        const adminDoc = doc(db, "admin_users", adminId);
        const adminSnap = await getDoc(adminDoc);

        if (!adminSnap.exists()) {
            throw new Error('Admin user not found');
        }

        const adminData = adminSnap.data();
        const newOrgAdmins = adminData.organizationId.filter(id => id !== organization.organizationId);

        // Update admin_users collection
        await updateDoc(adminDoc, {
            organizationId: newOrgAdmins
        });

      toast.success('Admin removed successfully');
      fetchAdmins();
    } catch (err) {
        console.error(err);
      toast.error('Failed to remove admin');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gray-900 rounded-xl p-6 w-full max-w-2xl"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Manage Organization Admins</h2>
            
            <form onSubmit={handleAddAdmin} className="mb-8">
              <div className="flex gap-4">
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg"
                >
                  Add Admin
                </button>
              </div>
            </form>

            {loading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {admins.map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div className="flex items-center">
                      <div>
                        <p className="text-white font-medium">{admin.displayName}</p>
                        <p className="text-gray-400 text-sm">{admin.email}</p>
                      </div>
                    </div>
                    {admin.id !== auth.currentUser.uid && (
                      <button
                        onClick={() => handleRemoveAdmin(admin.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={onClose}
              className="mt-6 px-4 py-2 text-gray-400 hover:text-white"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SettingsCard = ({ title, description, icon, onClick, className }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`p-6 rounded-xl cursor-pointer ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center mb-4">
        {icon}
        <h2 className="text-xl font-bold text-white ml-3">{title}</h2>
      </div>
      <p className="text-gray-400">{description}</p>
    </motion.div>
  );

const SettingsSection = ( {organization} ) => {
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="p-6 bg-gray-900 rounded-xl shadow-xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-gray-400">Customize your JoltChat experience</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SettingsCard
            title="Organization Admins"
            description="Manage who can access and manage your organization on JoltChat"
            className="bg-gray-800"
            onClick={() => setIsAdminDialogOpen(true)}
            icon={
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 text-blue-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
              
            }
          />

          {/* <SettingsCard
            title="User Profile"
            description="Update your personal information and preferences"
            className="bg-gray-800"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          /> */}

          <SettingsCard
            title="Setup & Integration"
            description=<span className='text-white'>Complete your setup by connecting your Planning Center to the JoltChat service</span>
            className="bg-gradient-to-r from-red-500 to-orange-500"
            onClick={() => navigate('/finish-setup')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            }
          />

          {/* <SettingsCard
            title="Notifications"
            description="Configure how and when you receive notifications"
            className="bg-gray-800"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            }
          /> */}
        </div>

        <div className="mt-8 p-6 bg-gray-800 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Need Help?</h2>
              <p className="text-gray-400">Check out our documentation or contact support</p>
            </div>
            <a 
              href="mailto:hello@joltchat.com?subject=JoltChat Support Request" 
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors inline-block"
            >
              Contact Support
            </a>
          </div>
        </div>
      </motion.div>
      <AdminDialog
        isOpen={isAdminDialogOpen}
        onClose={() => setIsAdminDialogOpen(false)}
        organization={organization}
      />
    </div>
  );
};

export default SettingsSection;